import { AnimatePresence, motion } from "motion/react";
import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { X } from "lucide-react";

interface DeleteAccountModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  isDeleting: boolean;
}

export function DeleteAccountModal({
  isOpen,
  onClose,
  onConfirm,
  isDeleting,
}: DeleteAccountModalProps) {
  const [confirmText, setConfirmText] = useState("");
  const ref = useRef<HTMLElement | null>(null);

  // Find the nearest dark-mode container to portal into
  if (!ref.current) {
    ref.current = document.querySelector(".dark") ?? document.body;
  }

  // Reset input when modal closes
  useEffect(() => {
    if (!isOpen) setConfirmText("");
  }, [isOpen]);

  const isConfirmed = confirmText === "DELETE";

  return createPortal(
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className="fixed inset-0 z-50 flex items-center justify-center"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
        >
          {/* Backdrop */}
          <motion.div
            className="absolute inset-0 bg-black/50"
            onClick={isDeleting ? undefined : onClose}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          />

          {/* Panel */}
          <motion.div
            className="relative z-10 mx-4 w-full max-w-lg rounded-3xl border border-black bg-white p-8 dark:border-white/20 dark:bg-[#2A2A2A]"
            initial={{ scale: 0.95, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.95, opacity: 0 }}
            transition={{ duration: 0.2 }}
          >
            <div className="mb-6 flex items-center justify-between">
              <h2 className="font-heading text-2xl font-bold text-red-600 dark:text-red-400">
                Delete your account?
              </h2>
              {!isDeleting && (
                <button
                  onClick={onClose}
                  className="flex h-8 w-8 items-center justify-center rounded-lg text-black/40 transition hover:bg-black/5 hover:text-black dark:text-white/40 dark:hover:bg-white/5 dark:hover:text-white"
                >
                  <X className="h-5 w-5" />
                </button>
              )}
            </div>

            <p className="mb-4 text-sm text-black/70 dark:text-white/70">
              This action is permanent and cannot be undone. The following will
              be deleted:
            </p>

            <ul className="mb-6 list-disc space-y-1 pl-5 text-sm text-black/60 dark:text-white/60">
              <li>Your agent instance and configuration</li>
              <li>All chat history and threads</li>
              <li>All integrations (Telegram, etc.)</li>
              <li>Usage data and memories</li>
              <li>Your Kubernetes pod and resources</li>
            </ul>

            <div className="mb-6">
              <label
                htmlFor="delete-confirm"
                className="mb-2 block text-sm font-bold text-black/70 dark:text-white/70"
              >
                Type DELETE to confirm
              </label>
              <input
                id="delete-confirm"
                type="text"
                value={confirmText}
                onChange={(e) => setConfirmText(e.target.value)}
                disabled={isDeleting}
                placeholder="DELETE"
                className="w-full rounded-xl border border-black/20 bg-transparent px-4 py-2.5 text-sm text-black placeholder:text-black/30 focus:border-red-600 focus:outline-none dark:border-white/20 dark:text-white dark:placeholder:text-white/30 dark:focus:border-red-400"
                autoComplete="off"
              />
            </div>

            <div className="flex gap-3">
              <button
                onClick={onClose}
                disabled={isDeleting}
                className="flex-1 rounded-xl border border-black/20 px-4 py-2.5 text-sm font-bold text-black transition hover:bg-black/5 disabled:opacity-50 dark:border-white/20 dark:text-white dark:hover:bg-white/5"
              >
                Cancel
              </button>
              <button
                onClick={onConfirm}
                disabled={!isConfirmed || isDeleting}
                className="flex-1 rounded-xl bg-red-600 px-4 py-2.5 text-sm font-bold text-white transition hover:bg-red-700 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {isDeleting ? "Deleting..." : "Delete Account"}
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>,
    ref.current,
  );
}
